import { useState, type FC } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCustomerDiscounts } from 'queries';
import { CustomerDiscountList } from 'components/common/customer-discount-list';
import { Button, Space } from '@g2u/ui-kit';
import { postCustomerDiscounts } from 'queries';
import { Toast, parseErrors } from 'helpers';
import { AddDiscountModal } from 'components/common/add-discount-modal/add-discount-modal.component';
import { AddSystemDiscountModal } from 'components/common/add-system-discount-modal/add-system-discount-modal.component';
import { useDiscountData } from 'helpers';
import { CustomerPrepaidProductsList } from 'components/common/customer-prepaid-products-list';
import { useCustomerProducts } from '../hooks/useCustomerProducts';
import { PrepaidProductStatus } from 'types';

type Props = Readonly<{
  customerId?: string;
}>;

enum DiscountType {
  SystemDiscount = 'system_discount',
  CustomDiscount = 'custom_discount',
}

export const CustomerDiscounts: FC<Props> = ({ customerId }) => {
  const { data: customerDiscounts, refetch: updateCustomerDiscounts } = useQuery(
    ['discounts', customerId],
    getCustomerDiscounts,
  );
  const { discountReasons, discountTypeTemplates, discountApprovers, systemDiscounts } = useDiscountData();

  const [isAddSystemDiscountModalOpen, setAddSystemDiscountModalOpen] = useState(false);
  const [isAddDiscountModalOpen, setAddDiscountModalOpen] = useState(false);

  const { mutate: addSystemDiscountMutation, isLoading } = useMutation(postCustomerDiscounts, {
    onSuccess: () => {
      updateCustomerDiscounts();
      setAddSystemDiscountModalOpen(false);
      setAddDiscountModalOpen(false);
    },
    onError: (error) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const handleAddDiscount = (values: any, discountType: DiscountType) => {
    let discount;

    switch (discountType) {
      case DiscountType.SystemDiscount:
        discount = {
          system_discount_id: values.type,
          approved_by: values.approver,
        };
        break;

      case DiscountType.CustomDiscount:
        discount = {
          discount_type_template_id: values.type,
          amount: values.amount,
          reason_id: values.reason,
          approved_by: values.approver,
        };
        break;

      default:
        throw new Error('Invalid discount type');
    }

    addSystemDiscountMutation({
      customerId,
      discounts: [discount],
    });
  };

  const { products, transferProducts, isProductsLoading, updateCustomerProductStatus } =
    useCustomerProducts(customerId);
  const handleProductTransfer = (productId: number, customerId: number, name: string) => {
    transferProducts(productId, customerId, name);
  };

  const handleUpdateCustomerProductStatus = (customerProductId: number, status: PrepaidProductStatus) => {
    updateCustomerProductStatus(customerProductId, status);
  };

  return (
    <div>
      <CustomerDiscountList refetch={updateCustomerDiscounts} discounts={customerDiscounts || []} />
      <Space.$ direction={Space.Direction.Horizontal} justify={Space.Justify.End}>
        <Button.$ dataTestId="" onClick={() => setAddSystemDiscountModalOpen(true)}>
          Add System Discount
        </Button.$>
        <AddSystemDiscountModal
          isOpen={isAddSystemDiscountModalOpen}
          onCancel={() => setAddSystemDiscountModalOpen(false)}
          onOk={(values) => handleAddDiscount(values, DiscountType.SystemDiscount)}
          discountTypeTemplates={discountTypeTemplates || []}
          systemDiscounts={systemDiscounts}
          discountApprovers={discountApprovers}
          isLoading={isLoading}
        />
        <Button.$ dataTestId="" onClick={() => setAddDiscountModalOpen(true)}>
          Add Customer Discount
        </Button.$>
        <AddDiscountModal
          isOpen={isAddDiscountModalOpen}
          onCancel={() => setAddDiscountModalOpen(false)}
          onOk={(values) => handleAddDiscount(values, DiscountType.CustomDiscount)}
          discountReasons={discountReasons}
          discountTypeTemplates={discountTypeTemplates || []}
          discountApprovers={discountApprovers}
          isLoading={isLoading}
        />
      </Space.$>
      <br />
      <CustomerPrepaidProductsList
        products={products}
        onTransfer={handleProductTransfer}
        onStatusUpdate={handleUpdateCustomerProductStatus}
        isLoading={isProductsLoading}
      />
    </div>
  );
};
